import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  UserPlusIcon,
  ChevronDownIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import {
  UsersIcon,
  BuildingOffice2Icon,
  BoltIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import apiClient from '../../lib/apiClient';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  credits: number;
  email_verified_at: string | null;
  created_at: string;
  search_tasks_count: number;
  current_plan?: string | null;
}

interface Pagination {
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function getAvatarColor(id: number) {
  const palettes = [
    'bg-blue-200 text-blue-800',
    'bg-purple-200 text-purple-800',
    'bg-emerald-200 text-emerald-800',
    'bg-amber-200 text-amber-800',
    'bg-rose-200 text-rose-800',
    'bg-cyan-200 text-cyan-800',
  ];
  return palettes[id % palettes.length];
}

function getTier(user: User): { label: string; color: string } {
  if (user.current_plan) {
    const name = user.current_plan.toUpperCase();
    if (name.includes('SCALE'))  return { label: user.current_plan, color: 'bg-emerald-100 text-emerald-700' };
    if (name.includes('GROWTH')) return { label: user.current_plan, color: 'bg-purple-100 text-purple-700' };
    return { label: user.current_plan, color: 'bg-blue-100 text-blue-700' };
  }
  return { label: 'FREE', color: 'bg-slate-100 text-slate-600' };
}

function getStatus(user: User): { label: string; dot: string; text: string } {
  if (user.credits === 0 && !user.email_verified_at) return { label: 'SUSPENDED', dot: 'bg-red-500', text: 'text-red-700' };
  if (!user.email_verified_at) return { label: 'PENDING', dot: 'bg-slate-400', text: 'text-slate-600' };
  return { label: 'ACTIVE', dot: 'bg-emerald-500', text: 'text-emerald-700' };
}

function StatCard({
  label, value, sub, icon: Icon, accent, badge,
}: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; accent?: string; badge?: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent ?? 'bg-slate-100'}`}>
          <Icon className="w-5 h-5 text-inherit" />
        </div>
        {badge && (
          <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div>
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-xl sm:text-[28px] font-extrabold text-slate-900 leading-none tracking-tight break-all">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-[11px] text-slate-400 font-medium mt-1">{sub}</p>}
      </div>
    </div>
  );
}

export default function UserDirectory() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Credit modal
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditMode, setCreditMode] = useState<'add' | 'remove'>('add');
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Add user modal
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', initial_credits: '', role: 'user' });
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);

  // Remove user modal
  const [removeUser, setRemoveUser] = useState<User | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  const [totalRevenue, setTotalRevenue] = useState(0);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page, per_page: perPage };
    if (search) params.search = search;
    apiClient.get('/admin/users', { params })
      .then(({ data }) => {
        setUsers(data.data ?? []);
        setPagination({
          total: data.total,
          current_page: data.current_page,
          per_page: data.per_page,
          last_page: data.last_page,
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, search, perPage]);

  useEffect(() => {
    fetchUsers();
    // Fetch dashboard stats for total revenue
    apiClient.get('/admin/dashboard-stats')
      .then(({ data }) => setTotalRevenue(data.total_revenue ?? 0))
      .catch(() => {});
  }, [fetchUsers]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Client-side filter on top of server data
  const filteredUsers = users.filter(u => {
    const hasPlan = !!u.current_plan;
    const status = getStatus(u).label.toLowerCase();
    if (tierFilter === 'paid' && !hasPlan) return false;
    if (tierFilter === 'free' && hasPlan) return false;
    if (statusFilter !== 'all' && status !== statusFilter.toLowerCase()) return false;
    return true;
  });

  const openModal = (user: User, mode: 'add' | 'remove') => {
    setSelectedUser(user);
    setCreditMode(mode);
    setCreditAmount('');
    setFeedback('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setCreditAmount('');
    setFeedback('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const amount = parseInt(creditAmount, 10);
    if (!amount || amount <= 0) return;
    setSubmitting(true);
    const delta = creditMode === 'remove' ? -amount : amount;
    try {
      await apiClient.post(`/admin/users/${selectedUser.id}/adjust-credits`, { credits: delta });
      setFeedback('Credits updated successfully.');
      fetchUsers();
      setTimeout(closeModal, 800);
    } catch (err: any) {
      setFeedback(err?.response?.data?.error ?? 'Failed to update credits.');
    } finally {
      setSubmitting(false);
    }
  };

  const openAddModal = () => {
    setAddForm({ name: '', email: '', password: '', initial_credits: '', role: 'user' });
    setAddErrors({});
    setShowAddPassword(false);
    setAddOpen(true);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddErrors({});
    setAddSubmitting(true);
    try {
      await apiClient.post('/admin/users', {
        name: addForm.name,
        email: addForm.email,
        password: addForm.password,
        role: addForm.role,
        initial_credits: addForm.initial_credits ? parseInt(addForm.initial_credits, 10) : 0,
      });
      setAddOpen(false);
      fetchUsers();
    } catch (err: any) {
      const errs = err?.response?.data?.errors ?? {};
      const flat: Record<string, string> = {};
      Object.entries(errs).forEach(([k, v]) => { flat[k] = Array.isArray(v) ? (v as string[])[0] : String(v); });
      if (!Object.keys(flat).length) flat.general = err?.response?.data?.message ?? 'Failed to create user.';
      setAddErrors(flat);
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!removeUser) return;
    setRemoveSubmitting(true);
    try {
      await apiClient.delete(`/admin/users/${removeUser.id}`);
      setRemoveUser(null);
      fetchUsers();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Failed to delete user.');
    } finally {
      setRemoveSubmitting(false);
    }
  };

  // Pagination pages
  const renderPages = () => {
    if (!pagination) return null;
    const { current_page, last_page } = pagination;
    const pages: (number | '...')[] = [];

    if (last_page <= 7) {
      for (let i = 1; i <= last_page; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current_page > 3) pages.push('...');
      for (let i = Math.max(2, current_page - 1); i <= Math.min(last_page - 1, current_page + 1); i++) {
        pages.push(i);
      }
      if (current_page < last_page - 2) pages.push('...');
      pages.push(last_page);
    }
    return pages;
  };

  const handleExportUsers = () => {
    apiClient.get('/admin/users/export', { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Failed to export users.'));
  };

  const enterpriseTiers = users.filter(u => !!u.current_plan).length;
  const avgCredits = users.length > 0
    ? Math.round(users.reduce((s, u) => s + u.credits, 0) / users.length)
    : 0;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto flex flex-col gap-6 pb-20">

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
        <span>Admin</span>
        <span className="text-slate-300">/</span>
        <span className="text-brand-600 font-bold">User Management</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 tracking-tight">User Directory</h1>
          <p className="text-[13px] text-slate-500 mt-1">
            {loading
              ? 'Loading...'
              : `Manage and monitor ${pagination?.total.toLocaleString() ?? '—'} active user accounts across all subscription tiers.`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportUsers}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-slate-50 transition-colors shadow-sm"
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> Export Data
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 bg-brand-500 text-white px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/20"
          >
            <UserPlusIcon className="w-4 h-4" /> Add User
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Total Users"
          value={loading ? '—' : (pagination?.total ?? 0)}
          icon={UsersIcon}
          accent="bg-blue-50 text-brand-600"
          badge={loading ? undefined : '+12.5%'}
        />
        <StatCard
          label="Paid Plans"
          value={loading ? '—' : enterpriseTiers}
          icon={BuildingOffice2Icon}
          accent="bg-slate-100 text-slate-500"
          sub="Active subscriptions"
        />
        <StatCard
          label="Avg. Credits Used"
          value={loading ? '—' : avgCredits.toLocaleString()}
          icon={BoltIcon}
          accent="bg-slate-100 text-slate-500"
        />
        <StatCard
          label="Total Revenue"
          value={loading ? '—' : `$${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          icon={BanknotesIcon}
          accent="bg-emerald-50 text-emerald-500"
        />
      </div>

      {/* Filters + Count */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-widest">Filter by:</span>
          {/* Tier filter */}
          <div className="relative">
            <select
              value={tierFilter}
              onChange={e => setTierFilter(e.target.value)}
              className="appearance-none bg-white border border-slate-200 text-[13px] font-bold text-slate-700 pl-3 pr-8 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 cursor-pointer"
            >
              <option value="all">All Tiers</option>
              <option value="paid">Paid</option>
              <option value="free">Free</option>
            </select>
            <ChevronDownIcon className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          {/* Status filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="appearance-none bg-white border border-slate-200 text-[13px] font-bold text-slate-700 pl-3 pr-8 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 cursor-pointer"
            >
              <option value="all">Any Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
            <ChevronDownIcon className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search name or email..."
              value={search}
              onChange={handleSearchChange}
              className="pl-9 pr-3 py-2 bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 rounded-xl transition-colors w-52"
            />
          </div>
        </div>
        <p className="text-[12px] font-extrabold text-slate-500 uppercase tracking-widest shrink-0">
          Showing {loading ? '—' : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, pagination?.total ?? 0)}`} of {loading ? '—' : (pagination?.total ?? 0).toLocaleString()}
        </p>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-left min-w-[640px]">
          <thead>
            <tr className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-100 bg-slate-50/50">
              <th className="px-6 py-4">User Details</th>
              <th className="px-6 py-4">Join Date</th>
              <th className="px-6 py-4">Tier</th>
              <th className="px-6 py-4">Credits</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              Array.from({ length: perPage }).map((_, i) => (
                <tr key={i}>
                  {[1, 2, 3, 4, 5, 6, 7].map(j => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-4 bg-slate-100 animate-pulse rounded" style={{ width: `${35 + (j % 4) * 15}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-slate-400 text-sm font-medium">
                  No users found.
                </td>
              </tr>
            ) : filteredUsers.map(user => {
              const tier = getTier(user);
              const status = getStatus(user);
              const joinDate = new Date(user.created_at);

              return (
                <tr
                  key={user.id}
                  className="hover:bg-slate-50/60 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/users/${user.id}`)}
                >
                  {/* User Details */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-[12px] font-black ${getAvatarColor(user.id)}`}>
                        {getInitials(user.name)}
                      </div>
                      <div>
                        <p className="text-[13px] font-extrabold text-slate-900 leading-tight">{user.name}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  {/* Join Date */}
                  <td className="px-6 py-4 text-[12px] text-slate-700 font-medium whitespace-pre-line">
                    {joinDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  {/* Tier */}
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded ${tier.color}`}>
                      {tier.label}
                    </span>
                  </td>
                  {/* Credits */}
                  <td className="px-6 py-4 text-[13px] font-extrabold text-slate-900">
                    {user.credits.toLocaleString()}
                  </td>
                  {/* Status */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${status.dot}`} />
                      <span className={`text-[12px] font-bold ${status.text}`}>{status.label}</span>
                    </div>
                  </td>
                  {/* Actions */}
                  <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => navigate(`/users/${user.id}`)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        title="View details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openModal(user, 'add')}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                        title="Adjust credits"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => setRemoveUser(user)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Remove user"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        </div>
        {/* Pagination Footer */}
        {pagination && pagination.last_page > 1 && (
          <div className="px-3 sm:px-6 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between bg-slate-50/30 gap-3">
            {/* Page numbers */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.current_page === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[12px] font-bold"
              >
                ‹
              </button>
              {renderPages()?.map((p, i) =>
                p === '...' ? (
                  <span key={`dots-${i}`} className="w-8 h-8 flex items-center justify-center text-slate-400 text-[12px]">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-bold transition-colors ${
                      pagination.current_page === p
                        ? 'bg-brand-500 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setPage(p => Math.min(pagination.last_page, p + 1))}
                disabled={pagination.current_page === pagination.last_page}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[12px] font-bold"
              >
                ›
              </button>
            </div>
            {/* Per page */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest">Items per page:</span>
              <div className="relative">
                <select
                  value={perPage}
                  onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                  className="appearance-none bg-white border border-slate-200 text-[12px] font-bold text-slate-700 pl-2 pr-6 py-1.5 rounded-lg focus:outline-none cursor-pointer"
                >
                  {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <ChevronDownIcon className="w-3 h-3 text-slate-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Credit Adjust Modal */}
      {modalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h2 className="text-[16px] font-extrabold text-slate-900">
                {creditMode === 'add' ? 'Add Credits' : 'Remove Credits'}
              </h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 border border-slate-100 rounded-xl">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-black ${getAvatarColor(selectedUser.id)}`}>
                  {getInitials(selectedUser.name)}
                </div>
                <div>
                  <p className="text-[13px] font-bold text-slate-900">{selectedUser.name}</p>
                  <p className="text-[11px] text-slate-400">Balance: <span className="font-bold text-slate-700">{selectedUser.credits.toLocaleString()} credits</span></p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
                    Amount to {creditMode}
                  </label>
                  <input
                    type="number" min="1"
                    value={creditAmount}
                    onChange={e => setCreditAmount(e.target.value)}
                    placeholder="Enter amount..."
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[14px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                    required autoFocus
                  />
                </div>
                {feedback && (
                  <p className={`text-[12px] font-semibold ${feedback.includes('success') ? 'text-emerald-600' : 'text-red-600'}`}>
                    {feedback}
                  </p>
                )}
                <button
                  type="submit" disabled={submitting}
                  className={`w-full py-3 rounded-xl text-[13px] font-black uppercase tracking-widest text-white transition-colors disabled:opacity-60 ${
                    creditMode === 'add' ? 'bg-brand-500 hover:bg-brand-600' : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {submitting ? 'Updating...' : `Confirm — ${creditMode === 'add' ? '+' : '−'}${creditAmount || '0'} credits`}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div>
                <h2 className="text-[18px] font-extrabold text-slate-900">Add User</h2>
                <p className="text-[12px] text-slate-500 mt-0.5">Creates account with email pre-verified.</p>
              </div>
              <button onClick={() => setAddOpen(false)} className="text-slate-400 hover:text-slate-700 transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddUser} className="px-6 py-5 flex flex-col gap-4">
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. John Smith"
                  value={addForm.name}
                  onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
                {addErrors.name && <p className="mt-1 text-[11px] text-red-500 font-semibold">{addErrors.name}</p>}
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Email Address</label>
                <input
                  type="email"
                  placeholder="john@company.com"
                  value={addForm.email}
                  onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                  required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
                {addErrors.email && <p className="mt-1 text-[11px] text-red-500 font-semibold">{addErrors.email}</p>}
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showAddPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={addForm.password}
                    onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
                    required
                    minLength={8}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAddPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showAddPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
                {addErrors.password && <p className="mt-1 text-[11px] text-red-500 font-semibold">{addErrors.password}</p>}
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Initial Credits <span className="text-slate-300 font-medium normal-case">(optional)</span></label>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={addForm.initial_credits}
                  onChange={e => setAddForm(f => ({ ...f, initial_credits: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Role</label>
                <select
                  value={addForm.role}
                  onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 bg-white"
                >
                  <option value="user">User</option>
                  <option value="support">Support (admin)</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
                <p className="mt-1 text-[11px] text-slate-400">Admin / Support / Super Admin can access this dashboard.</p>
              </div>
              {addErrors.general && (
                <p className="text-[12px] text-red-500 font-semibold">{addErrors.general}</p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                <p className="text-[11px] text-slate-400">Email will be marked as verified automatically.</p>
              </div>
              <button
                type="submit"
                disabled={addSubmitting}
                className="w-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white py-3 rounded-xl text-[13px] font-bold transition-colors"
              >
                {addSubmitting ? 'Creating…' : 'Create User'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Remove User Confirmation */}
      {removeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
            <div className="px-6 py-5 flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h2 className="text-[17px] font-extrabold text-slate-900">Remove User</h2>
                <p className="text-[13px] text-slate-500 mt-1">
                  Are you sure you want to permanently delete{' '}
                  <span className="font-bold text-slate-800">{removeUser.name}</span>?
                  This cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 w-full pt-1">
                <button
                  onClick={() => setRemoveUser(null)}
                  disabled={removeSubmitting}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveUser}
                  disabled={removeSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[13px] font-bold transition-colors disabled:opacity-50"
                >
                  {removeSubmitting ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
